import {
  type ToastManager,
  Toast as ToastPrimitive,
} from "@base-ui/react/toast";
import * as React from "react";
import {
  Toast,
  ToastAction,
  ToastClose,
  type ToastData,
  ToastDescription,
  ToastPortal,
  ToastProvider,
  ToastTitle,
  type ToastVariant,
  ToastViewport,
} from "@/components/ui/primitives/Toast";

export interface ToastOptions {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  id?: string;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
  dismiss: (toastId?: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(
  undefined,
);

/** Renders the live toast list inside the viewport. */
function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager<ToastData>();
  return (
    <>
      {toasts.map((toast) => (
        <Toast key={toast.id} swipeDirection="right" toast={toast}>
          {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
          {toast.description && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
          <ToastAction />
          <ToastClose />
        </Toast>
      ))}
    </>
  );
}

export interface ToastContextProviderProps {
  children: React.ReactNode;
}

export function ToastContextProvider({ children }: ToastContextProviderProps) {
  const managerRef = React.useRef<ToastManager<ToastData> | null>(null);
  if (managerRef.current === null) {
    managerRef.current = ToastPrimitive.createToastManager<ToastData>();
  }
  const manager = managerRef.current;

  const contextValue = React.useMemo<ToastContextValue>(
    () => ({
      dismiss(toastId) {
        manager.close(toastId);
      },
      toast(options) {
        manager.add({
          actionProps: options.action
            ? {
                children: options.action.label,
                onClick: options.action.onClick,
              }
            : undefined,
          data: { variant: options.variant },
          description: options.description,
          id: options.id,
          timeout:
            options.duration === Number.POSITIVE_INFINITY
              ? 0
              : options.duration,
          title: options.title,
        });
      },
    }),
    [manager],
  );

  return (
    <ToastContext value={contextValue}>
      <ToastProvider toastManager={manager}>
        {children}
        <ToastPortal>
          <ToastViewport>
            <ToastList />
          </ToastViewport>
        </ToastPortal>
      </ToastProvider>
    </ToastContext>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastContextProvider");
  }
  return context;
}
